import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup, Service } from 'webapp-types';
import { GoalFormatType } from 'webapp/enums/enums';
import { copyObject } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import GoalsInsightsSubgoalsRowComponent from 'webapp/components/goals/insights/subgoals-row';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { Goal } from 'webapp/models-custom';

const MAX_GOAL_DEPTH = 4; // the top parent + 3 descendant levels

interface Args {
	model: Goal;
}

type Action = 'openGoalSelector' | 'openCreateNewGoal';

export default class GoalInsightsSubgoalsComponent extends Component<Args> {
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('popup') declare popupService: Service.Popup;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('timeframe') declare timeframeService: Service.Timeframe;

	@tracked expandedIds: number[] = [];

	get title() {
		return this.args.model.isSubgoalsProgressGoal ? 'Sub-goals' : 'Aligned goals';
	}

	get goals() {
		return this.args.model.children;
	}

	get isEmpty() {
		return this.goals.length === 0;
	}

	@cached
	get orderedGoalData() {
		return this.mmGoalsService.generateTreeDataForGoals(this.goals);
	}

	@action
	openGoalSelectorOptions(e: MouseEvent | TouchEvent) {
		if (!this.args.model.canHaveChildren) {
			this.infolayerService.warning('Only public goals can have sub-goals!');
			return;
		}

		if (this.args.model.depthInHierarchy >= MAX_GOAL_DEPTH) {
			this.infolayerService.warning(
				`Max number of sub-levels (${MAX_GOAL_DEPTH - 1}) reached!`,
			);
			return;
		}

		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.button');

		if (!attachTo) {
			return;
		}

		const popup = this.popupService.createListPopup(attachTo);

		const options: { icon: IconName; text: string; action: string }[] = [
			{
				icon: 'Target',
				text: 'Add existing goal',
				action: 'openGoalSelector',
			},
			{
				icon: 'Plus',
				text: 'New goal',
				action: 'openCreateNewGoal',
			},
		];

		if (popup) {
			popup.open({
				itemsList: options,
				doAction: (actionName: string) => {
					this[actionName as Action]();
				},
			});
		}
	}

	@action
	openGoalSelector() {
		// skipping goals that are already added as subgoals
		const skipGoals = [this.args.model, ...this.mmGoalsService.goalModelsAddedAsSubgoals];

		const allGoals = this.mmGoalsService.goalForSpaceModels;
		// also skip objectives & the ones that are not public
		skipGoals.push(
			...allGoals.filter((goal) => {
				return !goal.canBeAChild;
			}),
		);

		// disabling goals that do not match any parent's timeframe
		// allow everything on perpetual parent
		const disableGoals = this.args.model.timeframe
			? allGoals.filter((goal) => {
					return (
						!!goal.timeframe && // allow perpetuals though
						!this.timeframeService.isMatchingTimeframe(goal, this.args.model.timeframe)
					);
				})
			: [];

		this.mmGoalsService.openGoalSelectorPopup({
			selectedGoalMetricData: null,
			metricSetup: null,
			skipGoals,
			disableGoalsInfo: disableGoals.map((goal) => {
				return {
					model: goal,
					reason: 'Timeframe mismatch',
				};
			}),
			textLabel:
				'Only <strong>public goals</strong> can be added from the dropdown (no objectives or private goals).',
			onSave: (metricData: DSSetup.MetricData | null) => {
				if (metricData?.key) {
					const goal = this.mmGoalsService.getGoalForGoalKeyLong(metricData.key);
					if (goal) {
						this.addAsSubgoal(goal);
					}
				}
			},
			isGoalTypeObjectiveDisabled: true,
		});
	}

	@action
	openCreateNewGoal() {
		const model = this.mmGoalsService.createNewModel();
		this.mmGoalsService.openCreatePopup(
			model,
			'subgoal',
			(goal: Goal | null) => {
				if (goal) {
					this.addAsSubgoal(goal);
				}
			},
			true,
			true,
		);
	}

	private addAsSubgoal(goal: Goal) {
		// validation
		if (!goal.canBeAChild) {
			if (goal.isObjectiveType) {
				this.infolayerService.warning('Objective cannot be added as a sub-goal!');
			}
			if (!goal.isPublic) {
				this.infolayerService.warning('Only public goals can be added as sub-goals!');
			}
			return;
		}

		const parentGoal = this.args.model;

		if (
			!!goal.timeframe &&
			!this.timeframeService.isMatchingTimeframe(goal, parentGoal.timeframe)
		) {
			this.infolayerService.warning('Goal timeframe does not fit in the parent timeframe!');
			return;
		}

		// the newly added might have its own children too
		const newDepthInHierarchy = parentGoal.depthInHierarchy + 1 + goal.childrenDepth;
		if (newDepthInHierarchy > MAX_GOAL_DEPTH) {
			this.infolayerService.warning(
				`Max number of sub-levels (${MAX_GOAL_DEPTH - 1}) reached!`,
			);
			return;
		}

		const subGoals = copyObject(parentGoal.subGoals || []);
		subGoals.push({
			goalId: goal.numericId!,
			goalFormat: goal.formatType || GoalFormatType.NUMBER, // we fallback to number bcos BE also does that
			isTimeframeValid: true, // we already check this with isMatchingTimeframe above
		});
		parentGoal.subGoals = subGoals;

		void parentGoal.saveAndCatch();
	}

	@action
	toggleExpanded(id: number) {
		if (this.expandedIds.includes(id)) {
			const descendantIds = this.mmGoalsService.getDescendantIds(id, this.orderedGoalData);
			this.expandedIds = this.expandedIds.filter(
				(expandedId) => expandedId !== id && !descendantIds.includes(expandedId),
			);
		} else {
			this.expandedIds = [...this.expandedIds, id];
		}
	}

	@action
	removeSubgoal(model: Goal) {
		this.args.model.subGoals = this.args.model.subGoals?.filter(
			(subGoal) => subGoal.goalId !== model.numericId,
		);
		void this.args.model.saveAndCatch();
	}
	<template>
		<div class="goal-insights-subgoals">
			<div class="goal-insights-subgoals__top">
				<h3><CommonUiIconComponent @name="Target" @size="lg" />{{this.title}}</h3>
				<SubmitButtonComponent
					@text="Add goal"
					@theme="blue-outlined"
					@icon="Plus"
					@noDelay={{true}}
					@onTrigger={{this.openGoalSelectorOptions}}
				/>
			</div>

			{{#unless this.isEmpty}}
				<table class="goals-list flexible-table bigtable">
					<colgroup>
						<col width="50%" />
						<col />
						<col width="80" />
						<col width="50" />
					</colgroup>
					<tbody>
						{{#each this.orderedGoalData as |goalData|}}
							<GoalsInsightsSubgoalsRowComponent
								@model={{goalData.goal}}
								@parentId={{goalData.parentId}}
								@depth={{goalData.depth}}
								@expandedIds={{this.expandedIds}}
								@toggleExpanded={{this.toggleExpanded}}
								@remove={{this.removeSubgoal}}
							/>
						{{/each}}
					</tbody>
				</table>
			{{/unless}}

		</div>
	</template>
}
